<template>
  <div class="base-slider user-select-none">
    <div
      ref="slider"
      class="base-slider__content d-flex flex-nowrap overflow-x-auto overflow-y-hidden"
      @mousedown.left="isMoving = !$device.isMobile"
      @dragstart.prevent
      @scroll="$emit('scroll:slider')"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { isNumber } from 'lodash-es'
import { scrollToPosition } from '~/utils/scrollToPosition'

const props = defineProps({
  directionScrollTimeout: { type: Number, default: 400 },
})

defineEmits(['scroll:slider'])

const slider = ref<HTMLDivElement | null>(null)

const isMoving = ref(false)
const isScrollingByBtn = ref(false)

const CONTAINER_PADDING_OFFSET = 8
const SEARCH_NEXT_ELEMENT_OFFSET = CONTAINER_PADDING_OFFSET + 40

const $device = useDevice()

onMounted(() => {
  if ($device.isMobile) {
    return
  }

  document.addEventListener('mouseup', () => {
    if ($device.isMobile) {
      return
    }

    if (slider.value?.contains(document.activeElement)) {
      const activeElement = document.activeElement as HTMLElement

      if (activeElement) {
        activeElement.blur()
      }
    }

    isMoving.value = false
  })

  document.addEventListener('mousemove', (ev) => {
    if (!isMoving.value) {
      return
    }

    if (slider.value && isNumber(slider.value.scrollLeft)) {
      slider.value.scrollLeft -= ev.movementX
    }
  })
})

async function handleDirectionScroll(direction: 'left' | 'right') {
  if (isScrollingByBtn.value || !slider.value) {
    return
  }

  const sliderBounds = slider.value?.getBoundingClientRect()
  const els = Array.from(slider.value?.querySelectorAll('.base-slider-item') as NodeListOf<HTMLDivElement>)

  if (direction === 'left') {
    els.reverse()
  }

  const scrollTarget = els.find((el) => {
    const boundingRect = el.getBoundingClientRect()

    if (direction === 'right') {
      return boundingRect.left > sliderBounds.left + SEARCH_NEXT_ELEMENT_OFFSET
    }

    return boundingRect.left < sliderBounds.left
  })

  if (!scrollTarget || !slider.value) {
    return
  }

  const scrollTargetBounds = scrollTarget.getBoundingClientRect()

  const scrollValue = direction === 'left'
    ? slider.value.scrollLeft - (sliderBounds.left - scrollTargetBounds.left + CONTAINER_PADDING_OFFSET)
    : slider.value.scrollLeft + (scrollTargetBounds.left - sliderBounds.left - CONTAINER_PADDING_OFFSET)

  scrollToPosition(scrollValue, {
    element: slider.value,
    horizontal: true,
    duration: props.directionScrollTimeout,
  })

  isScrollingByBtn.value = true

  await new Promise(resolve => setTimeout(() => resolve(true), props.directionScrollTimeout))

  isScrollingByBtn.value = false
}

defineExpose({
  handleDirectionScroll,
})
</script>

<style lang="scss">
.base-slider {
  position: relative;

  &__content {
    position: relative;
    z-index: 0;

    // для Firefox
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
}
</style>
